import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { grid } from "ldrs";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaChartBar, FaChartLine, FaChartPie, FaChartArea,
  FaDownload, FaFilePdf, FaFileExcel, FaFileCsv,
  FaFilter, FaSearch, FaEye, FaEyeSlash, FaChevronDown, FaChevronUp,
  FaCheckCircle, FaTimesCircle, FaClock, FaExclamationTriangle,
  FaRobot, FaBrain, FaLightbulb, FaTrendingUp, FaTrendingDown,
  FaIndustry, FaTag, FaUser, FaCalendarAlt, FaCertificate,
  FaArrowUp, FaArrowDown, FaMinus, FaPlus, FaExpandArrowsAlt,
  FaQuestionCircle, FaFileAlt
} from 'react-icons/fa';

grid.register();

const Reporting = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [isIFRSMessage, setIFRSMessage] = useState("");
  const [summary, setSummary] = useState({});
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedField, setSelectedField] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(""); // For Super Admin customer filter
  const [companyAdmins, setCompanyAdmins] = useState([]); // List of company admins (customers)
  const [expandedSections, setExpandedSections] = useState({});
  const [exporting, setExporting] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [gptReport, setGptReport] = useState("");
  const [showGptReport, setShowGptReport] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [timeRange, setTimeRange] = useState("all");
  const [selectedCardFilter, setSelectedCardFilter] = useState(null); // 'all', 'answered', 'completion', 'pending'
  const [expandedDimensions, setExpandedDimensions] = useState({}); // Track expanded dimensions
  const [analytics, setAnalytics] = useState({
    completionTrend: [],
    topicPerformance: [],
    userPerformance: [],
    sectorAnalysis: []
  });
  const reportsSectionRef = React.useRef(null);

  // Skeleton Components
  const SkeletonStatsCard = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 p-3 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-12 mb-1"></div>
          <div className="h-2 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full"></div>
      </div>
    </div>
  );

  const SkeletonChart = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 p-3 animate-pulse">
      <div className="flex items-center justify-between mb-3">
        <div className="h-4 bg-gray-200 rounded w-32"></div>
        <div className="flex space-x-1">
          <div className="h-6 w-12 bg-gray-200 rounded"></div>
          <div className="h-6 w-12 bg-gray-200 rounded"></div>
        </div>
      </div>
      <div className="h-32 flex items-end justify-between space-x-1">
        <div className="flex-1 bg-gray-200 rounded h-16"></div>
        <div className="flex-1 bg-gray-200 rounded h-20"></div>
        <div className="flex-1 bg-gray-200 rounded h-24"></div>
        <div className="flex-1 bg-gray-200 rounded h-28"></div>
        <div className="flex-1 bg-gray-200 rounded h-24"></div>
        <div className="flex-1 bg-gray-200 rounded h-20"></div>
      </div>
    </div>
  );

  const SkeletonReportCard = () => (
    <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden animate-pulse">
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1">
            <div className="w-6 h-6 bg-gray-200 rounded-lg"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="flex space-x-2">
                <div className="h-3 bg-gray-200 rounded w-20"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="h-8 bg-gray-200 rounded w-12"></div>
            <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    fetchTopics();
    fetchSummary();
    // Fetch company admins if user is superAdmin
    const userRole = localStorage.getItem('role');
    if (userRole === 'superAdmin') {
      fetchCompanyAdmins();
    } else if (userRole === 'companyAdmin') {
      // Company Admin: Automatically set their own ID as selected customer (default)
      const currentUserId = localStorage.getItem('userId');
      if (currentUserId) {
        setSelectedCustomer(currentUserId);
      }
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [selectedCustomer]);

  useEffect(() => {
    fetchReports();
  }, [selectedTopic, selectedField, selectedCustomer]);

  // Initialize IFRS message when component mounts
  useEffect(() => {
    if (reports.length > 0 && !isIFRSMessage) {
      setIFRSMessage(reports[0].isIFRSCertified || "");
    }
  }, [reports, isIFRSMessage]);

  const fetchTopics = async () => {
    try {
      const response = await axios.get("topics");
      if (response.data.status) {
        setTopics(response.data.topics || []);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      toastr.error(t('reporting.failedToFetchTopics'));
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      const userRole = localStorage.getItem('role');
      // Add customer filter for Super Admin or Company Admin (their own company)
      if (selectedCustomer) {
        params.append("userId", selectedCustomer);
      } else if (userRole === 'companyAdmin') {
        // Company Admin: Always filter by their own ID
        const currentUserId = localStorage.getItem('userId');
        if (currentUserId) {
          params.append("userId", currentUserId);
        }
      }
      
      const response = await axios.get(`summary?${params.toString()}`);
      if (response.data.status) {
        setSummary(response.data.summary || {});
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      toastr.error(t('reporting.failedToFetchSummary'));
    }
  };

  const fetchCompanyAdmins = async () => {
    try {
      const response = await axios.get("company-admins");
      if (response.data.status) {
        setCompanyAdmins(response.data.companyAdmins || []);
      }
    } catch (error) {
      console.error("Error fetching company admins:", error);
      toastr.error(t('reporting.failedToFetchCompanyAdmins'));
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedTopic) params.append("topic", selectedTopic);
      if (selectedField) params.append("field", selectedField);
      const userRole = localStorage.getItem('role');
      // Add customer filter for Super Admin or Company Admin (their own company)
      if (selectedCustomer) {
        params.append("userId", selectedCustomer);
      } else if (userRole === 'companyAdmin') {
        // Company Admin: Always filter by their own ID
        const currentUserId = localStorage.getItem('userId');
        if (currentUserId) {
          params.append("userId", currentUserId);
        }
      }

      const response = await axios.get(`reports?${params.toString()}`);
      if (response.data.status) {
        // Safely access isIFRSCertified from the first report if it exists
        if (response.data.reports && response.data.reports.length > 0) {
          setIFRSMessage(response.data.reports[0].isIFRSCertified || "");
        } else {
          setIFRSMessage("");
        }
        setReports(response.data.reports || []);
      } else {
        toastr.error(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toastr.error(t('reporting.failedToFetchReports'));
    } finally {
      setLoading(false);
    }
  };



  const exportToPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectedTopic) params.append("topic", selectedTopic);
      if (selectedField) params.append("field", selectedField);

      const response = await axios.get(`export-pdf?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'question-answers-report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toastr.success(t('reporting.pdfExportedSuccessfully'));
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toastr.error(t('reporting.failedToExportPDF'));
    } finally {
      setExporting(false);
    }
  };

  const toggleSection = (reportKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [reportKey]: !prev[reportKey]
    }));
  };
  
  // Generate unique key for each report
  const getReportKey = (report, index) => {
    // Use a combination of topic, field, and index to ensure uniqueness
    return `${report.disclosureTopic}-${report.disclosureField}-${index}`;
  };

  const getAnswerTypeColor = (answerType) => {
    switch (answerType) {
      case 'true':
        return 'bg-green-100 text-green-800';
      case 'false':
        return 'bg-red-100 text-red-800';
      case 'progress':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAnswerTypeText = (answerType) => {
    switch (answerType) {
      case 'true':
        return 'Yes';
      case 'false':
        return 'No';
      case 'progress':
        return 'In Progress';
      default:
        return 'Not Answered';
    }
  };

  const generateGPTReport = async () => {
    setGeneratingReport(true);
    try {
      const params = new URLSearchParams();
      if (selectedTopic) params.append("topic", selectedTopic);
      if (selectedField) params.append("field", selectedField);

      const response = await axios.post(`generate-gpt-report?${params.toString()}`);
      if (response.data.status) {
        setGptReport(response.data.report);
        setShowGptReport(true);
        toastr.success(t('reporting.aiReportGeneratedSuccessfully'));
      } else {
        toastr.error(response.data.message);
      }
    } catch (error) {
      console.error("Error generating GPT report:", error);
      toastr.error(t('reporting.failedToGenerateAIReport'));
    } finally {
      setGeneratingReport(false);
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectedTopic) params.append("topic", selectedTopic);
      if (selectedField) params.append("field", selectedField);

      const response = await axios.get(`export-excel?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'esg-report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toastr.success(t('reporting.excelExportedSuccessfully'));
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toastr.error(t('reporting.failedToExportExcel'));
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      const csvContent = [
        ['Topic', 'Field', 'Question', 'Answer', 'Status', 'User', 'Date'],
        ...reports.slice(1).flatMap(report =>
          report.questions.map(question => [
            report.disclosureTopic,
            report.disclosureField,
            question.engagementQuestion,
            question.answer || 'No answer',
            getAnswerTypeText(question.answerType),
            report.assignedUser?.name || 'Unknown',
            question.answeredAt ? new Date(question.answeredAt).toLocaleDateString() : 'Not answered'
          ])
        )
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'esg-report.csv';
      a.click();
      window.URL.revokeObjectURL(url);

      toastr.success(t('reporting.csvExportedSuccessfully'));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toastr.error(t('reporting.failedToExportCSV'));
    } finally {
      setExporting(false);
    }
  };

  const calculateAnalytics = () => {
    if (reports.length <= 1) return;

    const reportData = reports.slice(1);

    // Calculate completion trend dynamically from actual data
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get last 6 months
    const now = new Date();
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last6Months.push({
        month: monthNames[date.getMonth()],
        monthIndex: date.getMonth(),
        year: date.getFullYear(),
        date: date
      });
    }

    // Use total questions from summary (same as Total Questions card)
    const totalQuestions = summary.totalQuestions || 0;

    // Calculate answered questions per month (not cumulative)
    // For each month, count how many questions were answered specifically in that month
    const completionTrend = last6Months.map(({ month, monthIndex, year }) => {
      let completed = 0;

      reportData.forEach(report => {
        report.questions.forEach(question => {
          // Check if question was answered in this specific month
          if (question.answeredAt) {
            const answerDate = new Date(question.answeredAt);
            const monthStart = new Date(year, monthIndex, 1); // First day of the month
            const monthEnd = new Date(year, monthIndex + 1, 0); // Last day of the month
            
            // Count as answered if:
            // 1. Answered in this specific month (not cumulative)
            // 2. Has valid answer (text answer OR answerType !== 'progress')
            const hasAnswer = question.answer && question.answer.trim() !== '';
            const hasDefinitiveAnswer = question.answerType && question.answerType !== 'progress';
            
            if (answerDate >= monthStart && answerDate <= monthEnd &&
                (hasAnswer || hasDefinitiveAnswer)) {
              completed++;
            }
          }
        });
      });

      return {
        month,
        completed,
        total: totalQuestions || 1 // Prevent division by zero
      };
    });

    // Topic performance - User rankings grouped by sustainability dimension
    const dimensionUserStats = {};
    
    reportData.forEach(report => {
      const dimension = report.disclosureField || 'Unknown Dimension';
      const userName = report.assignedUser?.name || 'Unknown';
      
      // Count as answered if it has text answer OR definitive answer type ('true' or 'false')
      // This matches the backend logic
      const answeredCount = report.questions.filter(q => {
        const hasAnswer = q.answer && q.answer.trim() !== '';
        const hasDefinitiveAnswer = q.answerType && q.answerType !== 'progress';
        return hasAnswer || hasDefinitiveAnswer;
      }).length;
      
      const totalCount = report.totalQuestions;
      const pendingCount = totalCount - answeredCount;
      
      if (!dimensionUserStats[dimension]) {
        dimensionUserStats[dimension] = {};
      }
      
      if (!dimensionUserStats[dimension][userName]) {
        dimensionUserStats[dimension][userName] = {
          answeredCount: 0,
          totalCount: 0,
          pendingCount: 0
        };
      }
      
      dimensionUserStats[dimension][userName].answeredCount += answeredCount;
      dimensionUserStats[dimension][userName].totalCount += totalCount;
      dimensionUserStats[dimension][userName].pendingCount += pendingCount;
    });
    
    // Convert to array format with rankings
    const topicPerformance = Object.entries(dimensionUserStats).map(([dimension, users]) => {
      const userRankings = Object.entries(users)
        .map(([name, stats]) => {
          const pendingPercentage = stats.totalCount > 0 
            ? Math.round((stats.pendingCount / stats.totalCount) * 100)
            : 0;
          return {
            name,
            answerCount: stats.answeredCount,
            totalCount: stats.totalCount,
            pendingCount: stats.pendingCount,
            pendingPercentage
          };
        })
        .sort((a, b) => b.answerCount - a.answerCount)
        .slice(0, 3); // Top 3 users per dimension
      
      return {
        dimension,
        userRankings
      };
    }).filter(item => item.userRankings.length > 0);

    // User performance
    const userPerformance = reportData.reduce((acc, report) => {
      const userName = report.assignedUser?.name || 'Unknown';
      if (!acc[userName]) {
        acc[userName] = { completed: 0, total: 0 };
      }
      acc[userName].completed += report.questions.filter(q => q.answerType === 'true').length;
      acc[userName].total += report.totalQuestions;
      return acc;
    }, {});

    const userPerformanceArray = Object.entries(userPerformance).map(([name, data]) => ({
      name,
      completed: data.completed,
      total: data.total,
      percentage: Math.round((data.completed / data.total) * 100)
    }));

    // Sector analysis
    const sectorAnalysis = reportData.reduce((acc, report) => {
      report.questions.forEach(question => {
        if (!acc[question.sector]) {
          acc[question.sector] = { completed: 0, total: 0 };
        }
        acc[question.sector].total++;
        if (question.answerType === 'true') {
          acc[question.sector].completed++;
        }
      });
      return acc;
    }, {});

    const sectorAnalysisArray = Object.entries(sectorAnalysis).map(([sector, data]) => ({
      sector,
      completed: data.completed,
      total: data.total,
      percentage: Math.round((data.completed / data.total) * 100)
    }));

    setAnalytics({
      completionTrend,
      topicPerformance,
      userPerformance: userPerformanceArray,
      sectorAnalysis: sectorAnalysisArray
    });
  };

  useEffect(() => {
    calculateAnalytics();
  }, [reports]);

  const uniqueTopics = [...new Set(topics.map(t => t.topic))];
  const uniqueFields = [...new Set(topics.map(t => t.field))];

  // Filter reports based on selected card, search term, topic, and field
  const getFilteredReports = () => {
    if (reports.length <= 1) return [];
    
    let reportData = reports.slice(1);
    
    // Apply topic filter
    if (selectedTopic) {
      reportData = reportData.filter(report => report.disclosureTopic === selectedTopic);
    }
    
    // Apply field filter
    if (selectedField) {
      reportData = reportData.filter(report => report.disclosureField === selectedField);
    }
    
    // Apply card filter
    if (selectedCardFilter) {
      switch (selectedCardFilter) {
        case 'all':
          // Keep all reports
          break;
        case 'answered':
          // Show only reports with answered questions
          reportData = reportData.filter(report => 
            report.questions.some(q => q.answer && q.answer.trim() !== '')
          );
          break;
        case 'pending':
          // Show only reports with pending/unanswered questions
          reportData = reportData.filter(report => 
            report.questions.some(q => !q.answer || q.answer.trim() === '' || q.answerType === 'progress')
          );
          break;
        case 'completion':
          // Show all reports (same as all)
          break;
        default:
          break;
      }
    }
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      reportData = reportData.filter(report => {
        const topicMatch = report.disclosureTopic?.toLowerCase().includes(searchLower);
        const fieldMatch = report.disclosureField?.toLowerCase().includes(searchLower);
        const userMatch = report.assignedUser?.name?.toLowerCase().includes(searchLower);
        const standardsMatch = report.standards?.toLowerCase().includes(searchLower);
        
        return topicMatch || fieldMatch || userMatch || standardsMatch;
      });
    }
    
    return reportData;
  };

  const filteredReports = getFilteredReports();

  // Calculate topic completion breakdown for answered filter
  const getTopicCompletionBreakdown = () => {
    if (reports.length <= 1) return [];
    
    const reportData = reports.slice(1);
    const topicStats = {};
    
    reportData.forEach(report => {
      const topic = report.disclosureTopic;
      if (!topicStats[topic]) {
        topicStats[topic] = {
          topic,
          totalQuestions: 0,
          answeredQuestions: 0,
          completionRate: 0
        };
      }
      
      const answered = report.questions.filter(q => q.answer && q.answer.trim() !== '').length;
      topicStats[topic].totalQuestions += report.totalQuestions;
      topicStats[topic].answeredQuestions += answered;
    });
    
    // Calculate completion rates
    Object.values(topicStats).forEach(stat => {
      stat.completionRate = stat.totalQuestions > 0 
        ? Math.round((stat.answeredQuestions / stat.totalQuestions) * 100) 
        : 0;
    });
    
    // Sort by completion rate descending
    return Object.values(topicStats).sort((a, b) => b.completionRate - a.completionRate);
  };

  const topicCompletionBreakdown = selectedCardFilter === 'answered' ? getTopicCompletionBreakdown() : [];

  return (
    <div className="min-h-screen max-sm:w-[100vw] bg-gray-50 font-sans antialiased">
      {/* Header - Responsive */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-lg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow">
                <FaChartBar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{t('reporting.esgAnalyticsReporting')}</h1>
                <p className="text-green-100 text-xs sm:text-sm">{t('reporting.comprehensiveInsights')}</p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs text-green-100">{t('reporting.lastUpdated')}: {new Date().toLocaleDateString()}</p>
              <p className="text-sm text-lime-300 font-bold">{summary.totalQuestions || 0} {t('reporting.totalQuestions')}</p>
              <p className="text-xs text-green-200">{t('reporting.completion')}: {summary.completionRate || 0}%</p>
            </div>
          </div>
        </div>
      </div>
  
      {/* Compact Stats Cards - Responsive */}
      <div className="max-w-full mx-auto px-3 sm:px-4 py-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {loading ? (
            <>
              <SkeletonStatsCard />
              <SkeletonStatsCard />
              <SkeletonStatsCard />
              <SkeletonStatsCard />
            </>
          ) : (
            <>
              {/* Total Questions Card */}
              <div 
                className={`bg-white rounded-lg shadow border border-gray-100 p-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
                  selectedCardFilter === 'all' ? 'ring-2 ring-blue-500 border-blue-500' : ''
                }`}
                onClick={() => {
                  // Navigate to questions management page (only for Super Admin)
                  const userRole = localStorage.getItem('role');
                  if (userRole === 'superAdmin') {
                    navigate('/questions-management');
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('reporting.totalQuestions')}</p>
                    <p className="text-lg sm:text-xl font-bold text-blue-600">{summary.totalQuestions || 0}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <FaChartBar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                </div>
                {localStorage.getItem('role') === 'superAdmin' && (
                  <p className="text-xs text-gray-500 mt-2 text-center">Click For More Details, Below</p>
                )}
              </div>
      
              {/* Answered Questions Card */}
              <div 
                className={`bg-white rounded-lg shadow border border-gray-100 p-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
                  selectedCardFilter === 'answered' ? 'ring-2 ring-green-500 border-green-500' : ''
                }`}
                onClick={() => {
                  // Scroll to reports section
                  if (reportsSectionRef.current) {
                    reportsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                  // Also set filter to show answered questions
                  setSelectedCardFilter(selectedCardFilter === 'answered' ? null : 'answered');
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('reporting.answeredQuestions')}</p>
                    <p className="text-lg sm:text-xl font-bold text-green-600">{summary.totalAnswered || 0}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <FaCheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">Click For More Details, Below</p>
              </div>
      
              {/* Completion Rate Card */}
              <div 
                className={`bg-white rounded-lg shadow border border-gray-100 p-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
                  selectedCardFilter === 'completion' ? 'ring-2 ring-purple-500 border-purple-500' : ''
                }`}
                onClick={() => setSelectedCardFilter(selectedCardFilter === 'completion' ? null : 'completion')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('reporting.completionRate')}</p>
                    <p className="text-lg sm:text-xl font-bold text-purple-600">{summary.completionRate || 0}%</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <FaChartPie className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                </div>
              </div>
      
              {/* Pending Card */}
              <div 
                className={`bg-white rounded-lg shadow border border-gray-100 p-3 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
                  selectedCardFilter === 'pending' ? 'ring-2 ring-orange-500 border-orange-500' : ''
                }`}
                onClick={() => setSelectedCardFilter(selectedCardFilter === 'pending' ? null : 'pending')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('reporting.pendingQuestions')}</p>
                    <p className="text-lg sm:text-xl font-bold text-orange-600">{Math.max(0, (summary.totalQuestions || 0) - (summary.totalAnswered || 0))}</p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <FaClock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
  
        {/* Compact Search and Filter Section - Responsive */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-3 mb-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="flex flex-col md:flex-row items-center gap-3 flex-1 w-full">
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  placeholder={t('reporting.searchReports')}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoComplete="off"
                />
              </div>
  
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${showFilters
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <FaFilter className="w-3 h-3" />
                  <span className="hidden sm:inline">Filters</span>
                </button>
              </div>
            </div>
  
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (selectedCustomer) params.append("customer", selectedCustomer);
                  if (selectedTopic) params.append("topic", selectedTopic);
                  if (selectedField) params.append("field", selectedField);
                  navigate(`/section-91-report?${params.toString()}`);
                }}
                className="px-3 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center gap-2 text-sm"
              >
                <FaTag className="w-3 h-3" />
                {t('reporting.section91OnlyReport')}
              </button>
              <button
                type="button"
                onClick={() => {
                  // Pass current filter state to detailed-report via URL params
                  const params = new URLSearchParams();
                  if (selectedCustomer) params.append("customer", selectedCustomer);
                  if (selectedTopic) params.append("topic", selectedTopic);
                  if (selectedField) params.append("field", selectedField);
                  navigate(`/detailed-report?${params.toString()}`);
                }}
                className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2 text-sm"
              >
                <FaFileAlt className="w-3 h-3" />
                {t('reporting.report')}
              </button>
            </div>
          </div>
  
          {/* Advanced Filters - Responsive */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className={`grid grid-cols-1 ${localStorage.getItem('role') === 'superAdmin' ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-2`}>
                {/* Customer Filter - Only for Super Admin */}
                {localStorage.getItem('role') === 'superAdmin' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Customer</label>
                    <select
                      className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                      value={selectedCustomer}
                      onChange={(e) => setSelectedCustomer(e.target.value)}
                    >
                      <option value="">All</option>
                      {companyAdmins.map((admin) => (
                        <option key={admin.id} value={admin.id}>
                          {admin.companyName || admin.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
  
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Disclosure Topic</label>
                  <select
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                  >
                    <option value="">All Topics</option>
                    {uniqueTopics.map((topic, index) => (
                      <option key={index} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </select>
                </div>
  
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Disclosure Field</label>
                  <select
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    value={selectedField}
                    onChange={(e) => setSelectedField(e.target.value)}
                  >
                    <option value="">All Fields</option>
                    {uniqueFields.map((field, index) => (
                      <option key={index} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                </div>
  
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSelectedTopic('');
                      setSelectedField('');
                      setSelectedCustomer('');
                      setSearchTerm('');
                    }}
                    className="w-full px-2 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                  >
                    {t('reporting.clearFilters')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
  
        {/* IFRS Status Display - Responsive */}
        {isIFRSMessage && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mr-2">
                <FaCertificate className="w-3 h-3 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-800">IFRS Certification Status</h3>
                <p className="text-blue-700 text-xs">{isIFRSMessage}</p>
              </div>
            </div>
          </div>
        )}
  
        {/* Analytics Dashboard - Responsive */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
          {loading ? (
            <>
              <SkeletonChart />
              <SkeletonChart />
            </>
          ) : (
            <>
              {/* Completion Trend Chart */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                    <FaChartLine className="w-4 h-4 mr-2 text-green-600" />
                    Completion Trend
                  </h3>
                </div>
                <div className="h-32 flex items-end justify-between space-x-1">
                  {analytics.completionTrend.map((item, index) => (
                    <div key={index} className="flex flex-col items-center flex-1">
                      <div className="w-full bg-gray-200 rounded-t relative h-24 flex items-end">
                        <div
                          className="w-full bg-green-500 rounded-t transition-all duration-1000"
                          style={{ height: `${(item.completed / item.total) * 100}%` }}
                        ></div>
                      </div>
                      <div className="mt-1 text-xs text-gray-600 font-medium">{item.month}</div>
                      <div className="text-xs text-gray-500">{item.completed}/{item.total}</div>
                    </div>
                  ))}
                </div>
              </div>
      
              {/* Topic Performance Chart - User Rankings by Dimension */}
              <div className="bg-white rounded-lg shadow border border-gray-100 p-3 overflow-x-hidden">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center mb-3">
                  <FaChartPie className="w-4 h-4 mr-2 text-green-600" />
                  User Performance By Disclosure Topics
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto overflow-x-hidden">
                  {analytics.topicPerformance.length > 0 ? (
                    analytics.topicPerformance.map((item, index) => {
                      const dimensionKey = `dimension-${index}`;
                      const isExpanded = expandedDimensions[dimensionKey];
                      return (
                        <div key={index} className="border-b border-gray-100 pb-2 last:border-b-0">
                          <div 
                            className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-1 py-1 rounded transition-colors"
                            onClick={() => setExpandedDimensions(prev => ({
                              ...prev,
                              [dimensionKey]: !prev[dimensionKey]
                            }))}
                          >
                            <div className="flex items-center flex-1 min-w-0">
                              {isExpanded ? (
                                <FaChevronUp className="w-3 h-3 text-gray-500 mr-2 flex-shrink-0" />
                              ) : (
                                <FaChevronDown className="w-3 h-3 text-gray-500 mr-2 flex-shrink-0" />
                              )}
                              <span className="text-xs font-semibold text-gray-800 truncate" title={item.dimension}>
                                {item.dimension}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 flex-shrink-0 ml-2 whitespace-nowrap">
                              {item.userRankings.length} {item.userRankings.length === 1 ? 'user' : 'users'}
                            </span>
                          </div>
                          {isExpanded && (
                            <div className="mt-2 space-y-2 pl-5 overflow-x-hidden">
                              {item.userRankings.map((user, userIndex) => (
                                <div key={userIndex} className="bg-gray-50 rounded p-2 overflow-x-hidden">
                                  <div className="flex items-center justify-between mb-1 gap-2">
                                    <div className="flex items-center flex-1 min-w-0">
                                      <span className="text-xs font-medium text-gray-500 mr-2 flex-shrink-0 w-4">
                                        {userIndex + 1}.
                                      </span>
                                      <span className="text-xs font-medium text-gray-700 truncate" title={user.name}>
                                        {user.name}
                                      </span>
                                    </div>
                                    <span className="text-xs font-semibold text-green-600 flex-shrink-0 whitespace-nowrap">
                                      {user.answerCount} {user.answerCount === 1 ? 'answer' : 'answers'}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-gray-500 truncate">
                                      Pending: {user.pendingCount} of {user.totalCount}
                                    </span>
                                    <span className="text-xs font-semibold text-orange-600 flex-shrink-0 whitespace-nowrap">
                                      {user.pendingPercentage}% pending
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-xs text-gray-500 text-center py-4">
                      No user rankings available
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
  
        {/* Reports Section */}
        <div ref={reportsSectionRef}>
        {loading ? (
          <div className="space-y-3">
            <SkeletonReportCard />
            <SkeletonReportCard />
            <SkeletonReportCard />
            <SkeletonReportCard />
            <SkeletonReportCard />
          </div>
        ) : filteredReports.length > 0 ? (
          <div className="space-y-3">
            {selectedCardFilter && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaFilter className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="text-sm font-semibold text-blue-800">
{selectedCardFilter === 'all' ? t('reporting.showingAllQuestions') :
 selectedCardFilter === 'answered' ? t('reporting.showingAnsweredQuestions') :
 selectedCardFilter === 'pending' ? t('reporting.showingPendingQuestions') :
 t('reporting.showingCompletionData')} ({filteredReports.length} {filteredReports.length === 1 ? t('reporting.report') : t('reporting.reports')})
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedCardFilter(null)}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
{t('reporting.clearFilter')}
                  </button>
                </div>
              </div>
            )}
            
            {/* Topic Completion Breakdown for Answered Filter */}
            {selectedCardFilter === 'answered' && topicCompletionBreakdown.length > 0 && (
              <div className="bg-white rounded-lg shadow border border-gray-100 p-4 mb-3">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center mb-3">
                  <FaChartPie className="w-4 h-4 mr-2 text-green-600" />
                  Completion by Sustainability Dimensions
                </h3>
                <div className="space-y-2">
                  {topicCompletionBreakdown.map((topicStat, index) => (
                    <div key={index} className="border-b border-gray-100 pb-2 last:border-b-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700 truncate flex-1 mr-2" title={topicStat.topic}>
                          {topicStat.topic}
                        </span>
                        <span className="text-xs font-semibold text-green-600 flex-shrink-0">
                          {topicStat.completionRate}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${topicStat.completionRate}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">
                          {topicStat.answeredQuestions} of {topicStat.totalQuestions} answered
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {filteredReports.map((report, index) => {
              const reportKey = getReportKey(report, index);
              return (
              <div key={reportKey} className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden hover:shadow-md">
                <div
                  className="p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleSection(reportKey)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 flex-1">
                      <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                        <FaIndustry className="w-3 h-3 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-800 mb-1 truncate" title={`${report.disclosureTopic} - ${report.disclosureField}`}>
                          {report.disclosureTopic} - {report.disclosureField}
                        </h3>
                        <div className="flex flex-col xs:flex-row xs:items-center xs:space-x-2 space-y-1 xs:space-y-0 text-xs text-gray-600">
                          <span className="flex items-center">
                            <FaTag className="w-2.5 h-2.5 mr-1" />
                            {report.standards}
                          </span>
                          <span className="flex items-center">
                            <FaUser className="w-2.5 h-2.5 mr-1" />
                            {report.assignedUser?.name || 'Unknown'}
                          </span>
                          <span className="flex items-center">
                            <FaChartBar className="w-2.5 h-2.5 mr-1" />
                            {report.totalQuestions} questions
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-right">
                        <div className="text-xs text-gray-600">Completion</div>
                        <div className="text-sm font-bold text-green-600">
                          {report.totalQuestions > 0 
                            ? Math.round((report.questions.filter(q => q.answerType === 'true').length / report.totalQuestions) * 100)
                            : 0}%
                        </div>
                      </div>
                      <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                        {expandedSections[reportKey] ? (
                          <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                        ) : (
                          <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
  
                {expandedSections[reportKey] && (
                  <div className="border-t border-gray-200 p-3 bg-gray-50">
                    <div className="space-y-2">
                      {report.questions.map((question) => (
                        <div key={question.id} className="bg-white rounded p-2 border border-gray-200">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-800 mb-1 text-xs">
                                {question.questionIdentifier}
                              </h4>
                              <div className="flex flex-wrap items-center gap-1 text-xs text-gray-500 mb-2">
                                <span className="flex items-center">
                                  <FaIndustry className="w-2 h-2 mr-1" />
                                  {question.sector}
                                </span>
                                <span className="flex items-center">
                                  <FaTag className="w-2 h-2 mr-1" />
                                  {question.industry}
                                </span>
                                <span className="flex items-center">
                                  <FaCalendarAlt className="w-2 h-2 mr-1" />
                                  {question.topicCode}
                                </span>
                              </div>
                            </div>
                            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${getAnswerTypeColor(question.answerType)} self-start`}>
                              {getAnswerTypeText(question.answerType)}
                            </span>
                          </div>
  
                          <div className="mb-2">
                            <h5 className="font-medium text-gray-700 mb-1 flex items-center text-xs">
                              <FaQuestionCircle className="w-2.5 h-2.5 mr-1 text-blue-500" />
                              Question:
                            </h5>
                            <p className="text-gray-800 bg-blue-50 p-1.5 rounded text-xs">
                              {question.engagementQuestion}
                            </p>
                          </div>
  
                          <div>
                            <h5 className="font-medium text-gray-700 mb-1 flex items-center text-xs">
                              <FaCheckCircle className="w-2.5 h-2.5 mr-1 text-green-500" />
                              Answer:
                            </h5>
                            {question.answer ? (
                              <p className="text-gray-800 bg-green-50 p-1.5 rounded text-xs">
                                {question.answer}
                              </p>
                            ) : (
                              <p className="text-gray-500 italic bg-gray-50 p-1.5 rounded text-xs">
                                No answer provided
                              </p>
                            )}
                          </div>
  
                          {question.answeredAt && (
                            <div className="mt-1 flex items-center text-xs text-gray-500">
                              <FaCalendarAlt className="w-2 h-2 mr-1" />
                              Answered on: {new Date(question.answeredAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaChartBar className="w-6 h-6 text-white" />
            </div>
            <p className="text-gray-600 text-sm font-semibold mb-1">
{selectedCardFilter ? t('reporting.noReportsMatchFilter') : t('reporting.noReportsFound')}
            </p>
            <p className="text-gray-400 text-xs">
              {selectedCardFilter ? (
                <button
                  onClick={() => setSelectedCardFilter(null)}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
{t('reporting.clearSelectedFilter')}
                </button>
              ) : (
t('reporting.tryAdjustingSearch')
              )}
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default Reporting; 